import moment from "moment";
import type { CellObject, SheetData } from "write-excel-file/universal";

import type { ComplianceStatus } from "src/shared/api-types/certificate";

import certificateStatusService from "src/services/certificateStatus";
import { formatDateTime } from "src/services/formatters";

export type ExcelCell = CellObject | null;
export type ExcelRow = ExcelCell[];

export interface ExcelSheet {
  headerRows: ExcelRow[];
  bodyRows: ExcelRow[];
  // Column widths in characters, one per column
  columnWidths: number[];
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

interface SelectableOption {
  getId: () => string | number;
  getDisplayName: () => string;
}

const PART_SEPARATOR = " | ";
const VALUE_SEPARATOR = "; ";

// Light fills of the status icon colours (CourseStatusIcon), so the black text stays readable
const GREEN = { backgroundColor: "#C6E0B4", textColor: "#000000" };
const RED = { backgroundColor: "#F8CBAD", textColor: "#000000" };
const AMBER = { backgroundColor: "#FFE699", textColor: "#000000" };

const HEADER_COLOURS = { backgroundColor: "#1F3864", textColor: "#FFFFFF" };

const STATUS_COLOURS: Record<ComplianceStatus, { backgroundColor: string; textColor: string }> = {
  ok: GREEN,
  never_expires: GREEN,
  buffer: AMBER,
  expired: RED,
  missing: RED,
};

export const headerCell = (value: string, style: Partial<CellObject> = {}): CellObject => ({
  value,
  fontWeight: "bold",
  alignVertical: "center",
  ...HEADER_COLOURS,
  ...style,
});

// Days left until the validity date, counted like the Crew Overview's remaining days (calculateRemainingDays)
export const remainingDays = (validUntil: string | null | undefined): number | null =>
  validUntil ? moment(validUntil).diff(moment(), "days") + 1 : null;

// Valid and buffer statuses show their remaining days, as the Crew Overview icons do
const STATUSES_WITH_DAYS = new Set<string>(["ok", "buffer"]);

// A status cell has the status colour and shows the remaining days, or the status name where there are none.
// No status leaves the cell empty, like the screen.
export const statusCell = (status: string | null | undefined, t: Translate, days?: number | null): ExcelCell => {
  if (!status) return null;
  const showsDays = STATUSES_WITH_DAYS.has(status) && days !== null && days !== undefined;
  return {
    value: showsDays ? days : (certificateStatusService.getByCode(status)?.getDisplayName(t) ?? status),
    align: "center",
    // An unknown status shows green, as its icon does
    ...(STATUS_COLOURS[status as ComplianceStatus] ?? GREEN),
  };
};

// From the least to the most severe
const AGGREGATED_COLOURS = [GREEN, AMBER, RED];

// The index of the worst colour in AGGREGATED_COLOURS, or -1 without any status
const worstStatus = (cells: ExcelCell[]): number =>
  Math.max(
    -1,
    ...cells.map((cell) =>
      AGGREGATED_COLOURS.findIndex((colours) => colours.backgroundColor === cell?.backgroundColor),
    ),
  );

// The colours summarising a user's status cells: red if any is red, else amber if any is amber, else green.
// Without any status there are none.
export const aggregatedStatusColours = (cells: ExcelCell[]): Partial<CellObject> => {
  const worst = worstStatus(cells);
  return worst >= 0 ? { ...AGGREGATED_COLOURS[worst] } : {};
};

// A status cell with the validity date behind it, which the cell itself may not show
export interface ExportedStatus {
  cell: ExcelCell;
  validUntil: string | null | undefined;
}

const RED_INDEX = AGGREGATED_COLOURS.indexOf(RED);

// The earliest validity date among the statuses of the given colour. A red status without a date (missing) counts
// as the earliest, any other without a date (never expires) as the latest. The dates are ISO 8601, as the API sends
// them; one that does not parse counts as no date, so it cannot break the order of the others.
const earliestValidity = (statuses: ExportedStatus[], colour: number): number =>
  Math.min(
    ...statuses
      .filter(({ cell }) => worstStatus([cell]) === colour)
      .map(({ validUntil }) => {
        const date = validUntil ? moment(validUntil, moment.ISO_8601, true) : null;
        if (date?.isValid()) return date.valueOf();
        return colour === RED_INDEX ? -Infinity : Infinity;
      }),
  );

const compareValidity = (a: number, b: number): number => (a === b ? 0 : a < b ? -1 : 1);

// Red first, then amber, green and no status. Within a colour, the user whose worst status expired or expires
// first comes first; the order is otherwise kept.
export const worstStatusFirst = <T>(items: T[], statusesOf: (item: T) => ExportedStatus[]): T[] =>
  items
    .map((item) => {
      const statuses = statusesOf(item);
      const worst = worstStatus(statuses.map(({ cell }) => cell));
      return { item, worst, validity: worst >= 0 ? earliestValidity(statuses, worst) : 0 };
    })
    .sort((a, b) => b.worst - a.worst || compareValidity(a.validity, b.validity))
    .map(({ item }) => item);

// The display names of the selected options, in the options' order
export const selectedNames = (options: SelectableOption[], selectedIds: Array<string | number>): string[] => {
  const ids = new Set(selectedIds.map(String));
  return options.filter((option) => ids.has(String(option.getId()))).map((option) => option.getDisplayName());
};

// "<label>: <value>; <value>", or nothing for a filter with nothing selected
export const filterPart = (label: string, values: string[]): string | null =>
  values.length > 0 ? `${label}: ${values.join(VALUE_SEPARATOR)}` : null;

// The users filter is summarised as a count: the names would make the line unreadable
export const selectedUsersPart = (t: Translate, count: number): string | null =>
  count > 0 ? `${t("filter_users")}: ${t("excel_export.users_selected", { total: count })}` : null;

// Row 1 of every export: when the data was calculated, then the active filters
export const buildContextLine = (t: Translate, calculatedAt: string | null | undefined, parts: Array<string | null>) =>
  [
    calculatedAt
      ? t("cache_status.data_calculated_at", {
          time: formatDateTime(calculatedAt),
          interpolation: { escapeValue: false },
        })
      : null,
    ...parts,
  ]
    .filter((part): part is string => Boolean(part))
    .join(PART_SEPARATOR);

// Same file name pattern as the screenshot: the time is when the data was calculated
export const getExcelFileName = (reportName: string, calculatedAt: string | null | undefined) =>
  `${reportName}_${moment(calculatedAt || undefined).format("YYYY-MM-DD-HH-mm")}.xlsx`;

const downloadBlob = (blob: Blob, fileName: string) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
};

export const exportExcel = async ({
  reportName,
  calculatedAt,
  contextLine,
  sheet,
}: {
  reportName: string;
  calculatedAt: string | null | undefined;
  contextLine: string;
  sheet: ExcelSheet;
}): Promise<void> => {
  // Loaded on demand, so the library stays out of the main bundle
  const { default: writeExcelFile } = await import("write-excel-file/universal");

  const data: SheetData = [[{ value: contextLine }], ...sheet.headerRows, ...sheet.bodyRows];
  const blob = await writeExcelFile(data, {
    columns: sheet.columnWidths.map((width) => ({ width })),
    // Row 1 and the header rows stay in view while scrolling, like the sticky headers on screen
    stickyRowsCount: 1 + sheet.headerRows.length,
  }).toBlob();

  downloadBlob(blob, getExcelFileName(reportName, calculatedAt));
};
